import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Business } from '@mui/icons-material';
import { workosService } from '../services/workos';

interface WorkOSLoginButtonProps {
  organization?: string;
  onError?: (error: string) => void;
  fullWidth?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
}

const WorkOSLoginButton: React.FC<WorkOSLoginButtonProps> = ({ 
  organization, 
  onError,
  fullWidth = true,
  variant = 'contained',
  size = 'large',
}) => {
  const [loading, setLoading] = useState(false);

  const handleSSOLogin = async () => {
    setLoading(true);
    try {
      // Get the authorization URL from the backend
      const authUrl = await workosService.getAuthorizationUrl(organization, window.location.origin);
      
      // Redirect to WorkOS login
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Error initiating SSO login:', error);
      const errorMessage = error.message || 'Failed to initiate SSO login';
      
      if (onError) {
        onError(errorMessage);
      }
      
      setLoading(false);
    }
  };

  return (
    <Button
      fullWidth={fullWidth}
      variant={variant}
      size={size}
      onClick={handleSSOLogin}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Business />}
      sx={{
        py: 2,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
        color: 'white',
        boxShadow: '0 10px 30px rgba(124, 58, 237, 0.4), 0 0 40px rgba(124, 58, 237, 0.2)',
        textTransform: 'none',
        fontSize: '1.1rem',
        fontWeight: 700,
        position: 'relative',
        zIndex: 10,
        cursor: 'pointer',
        letterSpacing: '0.02em',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
          transition: 'left 0.5s ease',
        },
        '&:hover': {
          background: 'linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)',
          boxShadow: '0 15px 40px rgba(124, 58, 237, 0.5), 0 0 60px rgba(124, 58, 237, 0.3)',
          transform: 'translateY(-3px)',
          '&::before': {
            left: '100%',
          },
        },
        '&:active': {
          transform: 'translateY(-1px)',
        },
        '&:disabled': {
          background: 'rgba(124, 58, 237, 0.3)',
          color: 'rgba(255, 255, 255, 0.5)',
          boxShadow: 'none',
          cursor: 'not-allowed',
        },
        transition: 'all 0.3s ease',
      }}
    >
      {loading ? 'Connecting...' : 'Sign in with Ottonomy email'}
    </Button>
  );
};

export default WorkOSLoginButton;

